import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import { API_URL } from "./constants";
import "./FlyScraperTab.css";

// Admin Fly Scraper App: test and monitor the flight-price scraper
// (fly/scrape) without touching the AWS console. (This file's first
// deploy rode PR #176, whose merge hit a GitHub Actions outage that
// dropped the push event — this comment re-triggers the web deploy.)
// Three pieces:
//   - status: how fresh s3://activebeaches-fly-data/data/flights.json
//     is and how many flights it holds
//   - "Run scrape now": async-invokes the fly-scraper Lambda, then
//     polls status until the data file's timestamp changes (a run
//     takes ~5–15 minutes)
//   - cheapest-deals preview straight from the current data — also a
//     prototype of what members will eventually see.

const POLL_MS = 30000;
const MAX_POLLS = 40; // ~20 minutes

const fmtWhen = (raw) => {
  if (!raw) return "—";
  try {
    return new Date(raw).toLocaleString();
  } catch {
    return String(raw);
  }
};

const FlyScraperTab = () => {
  const { getAuthHeaders } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [runState, setRunState] = useState("idle"); // idle | starting | running | fresh
  const [runError, setRunError] = useState("");
  const [startedAt, setStartedAt] = useState(null);
  const baselineRef = useRef(null);
  const pollCountRef = useRef(0);
  const pollTimerRef = useRef(null);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const resp = await fetch(`${API_URL}?type=flyStatus`, {
        headers: getAuthHeaders(),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || `HTTP ${resp.status}`);
      setStatus(data);
      return data;
    } catch (err) {
      setError(err.message || "Couldn't load scraper status.");
      return null;
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    loadStatus();
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, [loadStatus]);

  const pollForFreshData = useCallback(async () => {
    pollCountRef.current += 1;
    const data = await loadStatus();
    const modified = data?.flights?.lastModified || null;
    if (modified && modified !== baselineRef.current) {
      setRunState("fresh");
      return;
    }
    if (pollCountRef.current >= MAX_POLLS) {
      setRunState("idle");
      setRunError(
        "No new data after ~20 minutes — check the fly-scraper CloudWatch logs."
      );
      return;
    }
    pollTimerRef.current = setTimeout(pollForFreshData, POLL_MS);
  }, [loadStatus]);

  const runScrape = async () => {
    setRunError("");
    setRunState("starting");
    baselineRef.current = status?.flights?.lastModified || null;
    pollCountRef.current = 0;
    try {
      const resp = await fetch(API_URL, {
        method: "POST",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ type: "flyRun" }),
      });
      const data = await resp.json();
      if (!resp.ok || !data.started) {
        throw new Error(
          [data.error, data.hint].filter(Boolean).join(" — ") ||
            `HTTP ${resp.status}`
        );
      }
      setStartedAt(Date.now());
      setRunState("running");
      pollTimerRef.current = setTimeout(pollForFreshData, POLL_MS);
    } catch (err) {
      setRunState("idle");
      setRunError(err.message || "Couldn't start the scrape.");
    }
  };

  const flights = status?.flights;
  const minsSinceStart = startedAt
    ? Math.round((Date.now() - startedAt) / 60000)
    : 0;

  return (
    <div className="fly-tab">
      <div className="fly-tab__head">
        <div>
          <h2 className="fly-tab__title">✈️ Fly Scraper</h2>
          <p className="fly-tab__sub">
            Flight-price scraper (fly/scrape) on AWS. Scheduled runs at
            ~6am/6pm ET; use the button to test a run right now. A run
            takes ~5–15 minutes — this page polls for fresh data.
          </p>
        </div>
        <div className="fly-tab__actions">
          <button
            type="button"
            className="fly-tab__btn"
            onClick={() => loadStatus()}
            disabled={loading}
          >
            {loading ? "Loading…" : "Refresh status"}
          </button>
          <button
            type="button"
            className="fly-tab__btn fly-tab__btn--primary"
            onClick={runScrape}
            disabled={runState === "starting" || runState === "running"}
          >
            {runState === "starting"
              ? "Starting…"
              : runState === "running"
              ? `Running… (~${minsSinceStart} min)`
              : "Run scrape now"}
          </button>
        </div>
      </div>

      {error && <div className="fly-tab__error">{error}</div>}
      {runError && <div className="fly-tab__error">{runError}</div>}
      {runState === "running" && (
        <div className="fly-tab__info">
          Scrape started — watching for new data every 30s. Leave this
          tab open, or check the ✈️ Slack summary / CloudWatch logs.
        </div>
      )}
      {runState === "fresh" && (
        <div className="fly-tab__success">
          🎉 Fresh data landed! The table below is the new scrape.
        </div>
      )}

      <div className="fly-tab__cards">
        <div className="fly-tab__card">
          <div className="fly-tab__card-label">Flights in data</div>
          <div className="fly-tab__card-value">
            {flights ? flights.count : "—"}
          </div>
        </div>
        <div className="fly-tab__card">
          <div className="fly-tab__card-label">Data last updated</div>
          <div className="fly-tab__card-value fly-tab__card-value--small">
            {fmtWhen(flights?.lastModified)}
          </div>
        </div>
        <div className="fly-tab__card">
          <div className="fly-tab__card-label">Last run log</div>
          <div className="fly-tab__card-value fly-tab__card-value--small">
            {status?.lastRun ? fmtWhen(status.lastRun.timestamp) : "—"}
          </div>
        </div>
      </div>

      {status?.flightsError && (
        <div className="fly-tab__warn">
          No flight data readable yet ({status.flightsError}).
          {String(status.flightsError).includes("AccessDenied")
            ? " The pending-join-requests Lambda role needs s3:GetObject on the data bucket."
            : " Run a scrape (here or locally with --push-to-s3) to populate it."}
        </div>
      )}

      {flights && flights.topDeals && flights.topDeals.length > 0 && (
        <>
          <h3 className="fly-tab__section">Cheapest deals right now</h3>
          <div className="fly-tab__table-wrap">
            <table className="fly-tab__table">
              <thead>
                <tr>
                  <th>From</th>
                  <th>To</th>
                  <th>Price</th>
                  <th>Dates</th>
                  <th>Seen</th>
                </tr>
              </thead>
              <tbody>
                {flights.topDeals.map((f, i) => (
                  <tr key={`${f.origin}-${f.destination}-${i}`}>
                    <td>{f.origin || "—"}</td>
                    <td>{f.destination || "—"}</td>
                    <td className="fly-tab__price">
                      {f.price < 999999 ? `$${f.price}` : "—"}
                    </td>
                    <td>{f.rawDates || "—"}</td>
                    <td className="fly-tab__muted">{fmtWhen(f.updatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {status?.lastRun && (
        <>
          <h3 className="fly-tab__section">Last run details</h3>
          <div className="fly-tab__kv">
            {Object.entries(status.lastRun).map(([k, v]) => (
              <div key={k} className="fly-tab__kv-row">
                <span className="fly-tab__kv-key">{k}</span>
                <span className="fly-tab__kv-val">
                  {typeof v === "object" ? JSON.stringify(v) : String(v)}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default FlyScraperTab;
